package co.maytes.standupbot.dashboard.pages.legal

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Alignment
import co.maytes.standupbot.dashboard.components.layout.LegalBullets
import co.maytes.standupbot.dashboard.components.layout.LegalLayout
import co.maytes.standupbot.dashboard.components.layout.LegalSection
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.motion.FadeInOnMount
import co.maytes.standupbot.dashboard.components.widgets.TocItem
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/legal/privacy")
@Composable
fun PrivacyPage() {
    MarketingShell {
        FadeInOnMount(delayMs = 0, contentAlignment = Alignment.TopCenter) {
            LegalLayout(
                title = "Privacy Policy",
                lastUpdated = "2026-10-23",
                summary = "We keep the data needed to build your daily standup, Slack + Linear workspace data, " +
                    "AI prompts and outputs, and billing metrics, nothing more. No ads, no third-party " +
                    "analytics, no selling. Data sits in Supabase Postgres (Sydney). Owners can export it from " +
                    "settings, and you can ask us to delete it.",
                toc = tocItems(),
            ) {
                SectionWhoWeAre()
                SectionWhatWeCollect()
                SectionHowWeUseIt()
                SectionWhereItLives()
                SectionRetention()
                SectionYourRights()
                SectionSecurity()
                SectionChanges()
            }
        }
    }
}

private fun tocItems(): List<TocItem> = listOf(
    TocItem("who-we-are", "Who we are"),
    TocItem("what-we-collect", "What we collect"),
    TocItem("how-we-use-it", "How we use it"),
    TocItem("where-it-lives", "Where it lives"),
    TocItem("retention", "Retention"),
    TocItem("your-rights", "Your rights"),
    TocItem("security", "Security"),
    TocItem("changes", "Changes"),
)

@Composable
private fun SectionWhoWeAre() {
    LegalSection(
        index = 1,
        anchor = "who-we-are",
        title = "Who we are",
        body = "StandupBot is operated by Maytes, an Australian software studio based in Sydney. When we " +
            "say \"we\" on this page we mean Maytes; when we say \"you\" we mean the admin installing " +
            "StandupBot and the teammates inside the workspace who post standups.",
    )
}

@Composable
private fun SectionWhatWeCollect() {
    LegalSection(
        index = 2,
        anchor = "what-we-collect",
        title = "What we collect",
        body = "Only the data we need to generate your daily standup and bill the workspace. Specifically:",
    ) {
        LegalBullets(
            listOf(
                "Slack workspace and user IDs (names, avatars, channel IDs), so we can post as the right bot user in the right channel.",
                "Linear activity scoped to the standups you pick, issues, cycles, status transitions, and comments. We do not pull Linear comments outside of those standups.",
                "AI synthesis inputs and outputs, the prompt we send to Anthropic and the standup we get back. The standup is kept so you can audit what the bot said on your behalf; the prompt and response text is removed with the raw activity (see Retention).",
                "Slack status text and emoji, while leave detection is on (it is on by default and an admin can turn it off in Settings), read so people who are away are left out of the standup. We store the status text, up to 160 characters, only for the runs where it applied.",
                "Billing usage metrics, token counts, per-workspace cents, number of standups posted.",
                "Application logs, structured entries with a traceId, scrubbed of message bodies.",
                "We record when setup steps complete (for example, when Slack is connected) to understand where setup is hard. This happens on our servers; we use no third-party analytics.",
            ),
        )
        P(attrs = { classes("mk-legal-lead") }) { Text("What we specifically do not collect:") }
        LegalBullets(
            listOf(
                "Passwords. Slack OAuth is our only sign-in mechanism.",
                "Payment card details. Stripe handles your card in their PCI compliant vault, and we only see a customer ID " +
                    "and invoice events.",
            ),
        )
    }
}

@Composable
private fun SectionHowWeUseIt() {
    LegalSection(
        index = 3,
        anchor = "how-we-use-it",
        title = "How we use it",
        body = "We use the data above for three things, and nothing else:",
    ) {
        LegalBullets(
            listOf(
                "Generating your daily standup, pulling Linear activity, synthesizing a human-readable summary, and posting it to Slack.",
                "Leaving people out of the standup while they are away, using their public Slack status, and showing admins who was left out and why.",
                "Aggregating cost and usage analytics so you can see what StandupBot actually costs per workspace and per user.",
                "Customer support, when you email us about a bug, we may read recent logs tagged with your workspace ID to reproduce it.",
            ),
        )
    }
}

@Composable
private fun SectionWhereItLives() {
    LegalSection(
        index = 4,
        anchor = "where-it-lives",
        title = "Where it lives",
        body = "StandupBot runs on a single Fly.io machine and stores data in Supabase Postgres in the " +
            "ap-southeast-2 (Sydney) region. AI synthesis requests are sent to the Anthropic API in the US. " +
            "Slack messages flow through the Slack API in the US. We do not operate a Redis cache, we do not " +
            "store data in S3, and we do not shard per-tenant, every workspace is a row-level tenant inside " +
            "the same Postgres cluster.",
    )
}

@Composable
private fun SectionRetention() {
    LegalSection(
        index = 5,
        anchor = "retention",
        title = "Retention",
        body = "Raw Linear and GitHub activity (the feed we pull to build each standup) and the text of the " +
            "prompts we send to Anthropic and the responses we get back are removed 30 days after each " +
            "standup by default. Workspace admins can set this anywhere from 7 to 90 days in workspace " +
            "settings, and every change is recorded in the audit log. Standup records, the summaries and " +
            "text we've posted, are kept for the life of the workspace so you can search them, and token " +
            "counts and cost are kept after prompt text is removed. Cancelling a paid plan does not delete " +
            "your data; deleting the workspace does.",
    )
}

@Composable
private fun SectionYourRights() {
    LegalSection(
        index = 6,
        anchor = "your-rights",
        title = "Your rights",
        body = "A workspace owner can download everything we hold on the workspace as a ZIP of data files " +
            "from Settings, and admins can download the full audit log as CSV or NDJSON. Secrets, tokens and " +
            "sessions are never included. For anything else, email us and we'll show you the data or hand it " +
            "back. A workspace owner can delete the workspace in Settings: everyone is signed out and " +
            "standups stop immediately, and after 30 days the workspace, its standups, summaries, " +
            "integration tokens and audit entries are permanently deleted. Reinstalling StandupBot from " +
            "Slack within those 30 days cancels the deletion. We keep only a one-way hash of the workspace " +
            "ID and the deletion date as proof it happened. Cancel any paid plan in Billing first, because " +
            "deletion is blocked while a subscription is active.",
    ) {
        P(attrs = { classes("mk-legal-body") }) {
            Text("Email ")
            Link(path = "mailto:privacy@standupbot.app", text = "privacy@standupbot.app", modifier = Modifier.classNames("mk-legal-link"))
            Text(". We reply within five business days.")
        }
    }
}

@Composable
private fun SectionSecurity() {
    LegalSection(
        index = 7,
        anchor = "security",
        title = "Security",
        body = "OAuth tokens (Slack, Linear, Anthropic) are encrypted at rest with AES-GCM using a key we " +
            "rotate quarterly. Sign-in tokens last 15 minutes and are signed with a secret we can rotate " +
            "without signing anyone out. TLS is enforced on every inbound and outbound hop. No human at Maytes " +
            "has a shared superuser database account, we connect with short-lived, per-engineer credentials " +
            "tied to an audit log.",
    )
}

@Composable
private fun SectionChanges() {
    LegalSection(
        index = 8,
        anchor = "changes",
        title = "Changes",
        body = "When we change this policy in a way that affects how your data is handled, we'll email " +
            "workspace admins at least 14 days before the change takes effect. Cosmetic edits (typos, " +
            "clarifications) go live without notice and are logged in our changelog.",
    )
}
